import { Alert, Box, Divider, Grid, Paper, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import {
  cancelChallenge,
  cancelLogin,
  loginChallenge,
  loginUser,
  resetPassword,
  resetPasswordChallenge,
  verifyUserPreAuth,
} from '@sophocles/features/auth/authActions';
import {
  getAuthStatus,
  getAuthUserPasswordVerifier,
  getCurrentChallenge,
  getIdentityProviders,
} from '@sophocles/features/auth/authSelectors';
import { selectTenantInfo } from '@sophocles/features/tenant/slice';
import BlueMotherboardImage from '@sophocles/public/blue-motherboard.jpg';
import LoginForm, {
  InitialPasswordForm,
  MultiFactorChallengeForm,
  ResetPasswordForm,
} from '@sophocles/views/auth/forms';

function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { t } = useTranslation();

  const identityProviders = useSelector(getIdentityProviders);
  const authStatus = useSelector(getAuthStatus);
  const storedUsername = useSelector(state => state.authentication.username);
  const verifyPassword = useSelector(getAuthUserPasswordVerifier);
  const challenge = useSelector(getCurrentChallenge);
  // Default to {} so an early render before initializeTenant resolves
  // doesn't crash on tenantInfo.organization_name etc.
  const tenantInfo = useSelector(selectTenantInfo) ?? {};

  const defaultSubtitle = useMemo(
    () =>
      t('auth.loginMessage', {
        tenantName: tenantInfo.organization_name,
      }),
    [tenantInfo.organization_name, t],
  );

  const [title, setTitle] = useState(t('auth.login'));
  const [subtitle, setSubtitle] = useState(defaultSubtitle);
  const [error, setError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [username, setUsername] = useState(storedUsername || '');

  useEffect(() => {
    if (storedUsername && !username) {
      setUsername(storedUsername);
    }
  }, [storedUsername]);

  const queryParams = new URLSearchParams(location.search);
  const urlError = queryParams.get('error');

  useEffect(() => {
    if (authStatus.isAuthenticated && user) {
      const redirect = location?.state?.from?.pathname || '/';
      navigate(redirect);
    }
  }, [authStatus.isAuthenticated, user]);

  const withSubmitting = async action => {
    setIsSubmitting(true);
    try {
      await action();
      setError(false);
    } catch {
      setError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitVerify = username => {
    setUsername(username);
    return withSubmitting(() => dispatch(verifyUserPreAuth(username)));
  };

  const handleSubmitLogin = (username, password) => {
    return withSubmitting(() => dispatch(loginUser(username, password)));
  };

  const handleSubmitMultiFactor = answer => {
    if (!challenge?.name || !challenge?.session) return;
    return withSubmitting(() => dispatch(loginChallenge(challenge.name, challenge.session, username, { answer })));
  };

  const handleInitiatePasswordReset = (isRequired, overrideEnrollment) => {
    const isLegacy = (authStatus.isLegacyAuth || !authStatus.isEnrolled) && !overrideEnrollment;
    setIsSubmitting(true);
    dispatch(resetPassword(username, isRequired, isLegacy));
    if (isLegacy) {
      dispatch(cancelChallenge());
    }
    setIsSubmitting(false);
  };

  const handleConfirmPasswordReset = (password, code) => {
    return withSubmitting(async () => {
      await dispatch(resetPasswordChallenge(username, password, code));
      await dispatch(loginUser(username, password));
    });
  };

  const handleSubmitPasswordChange = newPassword => {
    if (!challenge?.name || !challenge?.session) return;
    return withSubmitting(async () => {
      await dispatch(loginChallenge(challenge.name, challenge.session, username, { password: newPassword }));
      await dispatch(loginUser(username, newPassword));
    });
  };

  const handleCancel = () => {
    setIsSubmitting(false);
    setError(false);
    if (authStatus.challenge?.name) {
      dispatch(cancelChallenge());
    } else {
      dispatch(cancelLogin());
    }
  };

  useEffect(() => {
    const challengeName = authStatus?.challenge?.name;
    switch (challengeName) {
      case 'FORGOT_PASSWORD':
      case 'RESET_REQUIRED':
        setTitle('Reset Password');
        setSubtitle('Enter the confirmation code sent to your e-mail to reset your password');
        if (!authStatus.passwordReset?.inProgress) {
          handleInitiatePasswordReset(true, true);
        }
        break;
      case 'NEW_PASSWORD_REQUIRED':
        setTitle('Update Password');
        setSubtitle('Set a new password for accessing your account');
        break;
      case 'SMS_MFA':
        setTitle('Verify Identity');
        setSubtitle('Enter the confirmation code sent to you to verify your identity');
        break;
      default:
        setTitle(t('auth.login'));
        setSubtitle(defaultSubtitle);
        break;
    }
  }, [authStatus.challenge?.name]);

  // Sync subtitle when the tenant name resolves from the backend.
  // defaultSubtitle recomputes via useMemo when organization_name changes,
  // but the switch-case effect above only runs on challenge changes — so
  // the subtitle stays stale at the default challenge state.
  useEffect(() => {
    if (!authStatus?.challenge?.name) {
      setSubtitle(defaultSubtitle);
    }
  }, [defaultSubtitle, authStatus?.challenge?.name]);

  const renderForm = () => {
    const challengeName = authStatus?.challenge?.name;
    switch (challengeName) {
      case 'RESET_REQUIRED':
      case 'FORGOT_PASSWORD':
        return (
          <ResetPasswordForm
            error={error}
            challenge={authStatus.challenge}
            resetStatus={authStatus.passwordReset}
            isSubmitting={isSubmitting}
            onSubmit={handleConfirmPasswordReset}
            onCancel={handleCancel}
            verifyPassword={verifyPassword}
          />
        );
      case 'SMS_MFA':
        return (
          <MultiFactorChallengeForm
            error={error}
            challenge={authStatus.challenge}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmitMultiFactor}
            onCancel={handleCancel}
          />
        );
      case 'NEW_PASSWORD_REQUIRED':
        return (
          <InitialPasswordForm
            error={error}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmitPasswordChange}
            onCancel={handleCancel}
            verifyPassword={verifyPassword}
          />
        );
      default:
        return (
          <LoginForm
            error={error}
            isVerified={authStatus.isVerified}
            isSubmitting={isSubmitting}
            isEnabled={authStatus.isEnabled}
            identityProviders={authStatus.isEnrolled ? identityProviders : []}
            onVerify={handleSubmitVerify}
            onLogin={handleSubmitLogin}
            onCancel={handleCancel}
            onResetPassword={handleInitiatePasswordReset}
          />
        );
    }
  };

  useEffect(() => {
    document.title = `${tenantInfo.organization_name} - ${t('auth.login')}`;
    document.body.classList.add('no-body-padding');
    return () => {
      document.body.classList.remove('no-body-padding');
    };
  }, [tenantInfo.organization_name, t]);

  return (
    <Grid
      container
      spacing={0}
      sx={{
        overflow: 'hidden',
        backgroundImage: `url(${BlueMotherboardImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
    >
      <Grid
        size={12}
        sx={theme => ({
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.75)',
          p: { xs: '16px', sm: '32px', md: '32px 48px' },
          [theme.breakpoints.down('lg')]: {
            backgroundColor: 'rgba(0,0,0,0.75)',
          },
        })}
      >
        <Paper
          variant="elevation"
          elevation={2}
          sx={{
            zIndex: 2,
            maxWidth: '480px',
            width: '100%',
            p: { xs: '16px', sm: '32px', md: '32px 48px' },
            textAlign: 'center',
            transition: 'all 0.5s ease-in-out',
          }}
        >
          <Box>
            <Box>
              <div style={{ marginTop: '20px', marginBottom: '32px', display: 'flex', justifyContent: 'center' }}>
                <img src={tenantInfo.color_logo ?? tenantInfo.logo} height="48" alt="Logo" />
              </div>
              <Typography variant="h6" sx={{ mt: 2, fontWeight: 'bold', textTransform: 'uppercase' }}>
                {title}
              </Typography>
              {subtitle ? <Typography variant="caption">{subtitle}</Typography> : null}
            </Box>
            {urlError && (
              <Alert severity="error" sx={{ my: 3 }}>
                {urlError}
              </Alert>
            )}
            <Divider sx={{ mt: 3, mb: 4 }} />
            {renderForm()}
            {authStatus.message ? (
              <Typography variant="caption" color="red" sx={{ mt: '32px', textAlign: 'center' }}>
                {authStatus.message}
              </Typography>
            ) : null}
          </Box>
        </Paper>
        <Typography
          variant="caption"
          sx={{ textAlign: 'center', position: 'absolute', bottom: 16, width: '100%', zIndex: 1, color: 'white' }}
        >
          © {new Date().getFullYear()} {tenantInfo.organization_name_long}
        </Typography>
      </Grid>
    </Grid>
  );
}

export default LoginPage;
